import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, orderBy, query, deleteDoc, doc } from 'firebase/firestore';
import { firebaseApp } from '../../firebase';
import { Bell, Trash2, Users, Briefcase, Calendar, Mail } from 'lucide-react';
import toast from 'react-hot-toast';

const db = getFirestore(firebaseApp);

const ProPlusNotifyTab = () => {
  const [notifyRequests, setNotifyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, HR, JobSeeker

  useEffect(() => {
    fetchNotifyRequests();
  }, []);

  const fetchNotifyRequests = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'proPlusNotify'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));
      setNotifyRequests(requests);
    } catch (error) {
      console.error('Error fetching Pro Plus notify requests:', error);
      toast.error('Failed to load notification requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification request?')) return;
    
    try {
      await deleteDoc(doc(db, 'proPlusNotify', id));
      setNotifyRequests(prev => prev.filter(req => req.id !== id));
      toast.success('Request deleted successfully');
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request');
    }
  };

  const filteredRequests = filter === 'all' 
    ? notifyRequests 
    : notifyRequests.filter(req => req.role === filter);

  const stats = {
    total: notifyRequests.length,
    hr: notifyRequests.filter(r => r.role === 'HR').length,
    jobSeeker: notifyRequests.filter(r => r.role === 'JobSeeker').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="w-6 h-6 text-red-600" />
          <h2 className="text-2xl font-bold text-gray-900">Pro Plus Launch Notifications</h2>
        </div>
        <p className="text-gray-600 text-sm">
          Users who requested to be notified when Pro Plus plan launches
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <Bell className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">HR Recruiters</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.hr}</p>
            </div>
            <Briefcase className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Job Seekers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.jobSeeker}</p>
            </div>
            <Users className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              filter === 'all'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setFilter('HR')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              filter === 'HR'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            HR ({stats.hr})
          </button>
          <button
            onClick={() => setFilter('JobSeeker')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              filter === 'JobSeeker'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Job Seekers ({stats.jobSeeker})
          </button>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requested On
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    <Bell className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-lg font-medium">No notification requests yet</p>
                    <p className="text-sm mt-1">Users will appear here when they request Pro Plus notifications</p>
                  </td>
                </tr>
              ) : (
                filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{request.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        request.role === 'HR' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {request.role === 'HR' ? (
                          <Briefcase className="w-3 h-3" />
                        ) : (
                          <Users className="w-3 h-3" />
                        )}
                        {request.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        {request.createdAt.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDelete(request.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Info */}
      {filteredRequests.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800">
            💡 <strong>Tip:</strong> You can export these emails to your email marketing tool to notify users when Pro Plus launches.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProPlusNotifyTab;
